import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * TermCalendarManager — admin page for the UK term calendar AND
 * holiday-club windows.
 *
 * Two kinds are supported:
 *   - kind="term"          → school terms (autumn/spring/summer)
 *   - kind="holiday_club"  → gap windows (half-term breaks, Christmas,
 *                            Easter, Summer holidays). Bank-holiday
 *                            closures are auto-tagged when seeded.
 *
 * Capabilities:
 *   • Seed UK preset for an academic year (one click) — separately for
 *     terms vs. holiday clubs.
 *   • Edit start/end and a list of closed_dates (half-term ranges,
 *     inset days, bank holidays, other closures).
 *   • Add a custom term/holiday-club.
 *   • Remove a row.
 *
 * The calendar drives:
 *   - Term-time session generation on the Sessions tab (now also
 *     supports an optional "Start sessions from" date).
 *   - Parent recurring booking plans that opt into "Skip school
 *     holidays" (existing behaviour, reads from this collection).
 */
const PRESET_YEARS = ['2025-26', '2026-27'];
const TERMS_ORDER = ['autumn', 'spring', 'summer'];
const HOLIDAY_CLUB_ORDER = [
  'autumn_half_term',
  'christmas',
  'spring_half_term',
  'easter',
  'summer_half_term',
  'summer',
];
const CLOSED_TYPES = [
  { value: 'half_term', label: 'Half term' },
  { value: 'inset', label: 'Inset day' },
  { value: 'bank_holiday', label: 'Bank holiday' },
  { value: 'other', label: 'Other closure' },
];

const KIND_LABELS = {
  term: 'School terms',
  holiday_club: 'Holiday clubs',
};

export default function TermCalendarManager() {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seedingKey, setSeedingKey] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // term being edited (object) or {kind} for new

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getTerms();
      setTerms(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  // Group by kind first, then by academic year, with stable ordering of
  // both terms (autumn → spring → summer) and holiday-club windows.
  const grouped = useMemo(() => {
    const byKind = { term: {}, holiday_club: {} };
    for (const t of terms) {
      const kind = t.kind || 'term';
      const ay = t.academic_year || 'Other';
      if (!byKind[kind]) byKind[kind] = {};
      (byKind[kind][ay] ||= []).push(t);
    }
    const sortKeyFor = (kind, t) => {
      if (kind === 'holiday_club') {
        const idx = HOLIDAY_CLUB_ORDER.indexOf(t.term);
        return idx === -1 ? 999 : idx;
      }
      const idx = TERMS_ORDER.indexOf(t.term);
      return idx === -1 ? 999 : idx;
    };
    for (const kind of Object.keys(byKind)) {
      for (const ay of Object.keys(byKind[kind])) {
        byKind[kind][ay].sort((a, b) => sortKeyFor(kind, a) - sortKeyFor(kind, b));
      }
    }
    return byKind;
  }, [terms]);

  const handleSeed = async (year, kind) => {
    setSeedingKey(`${kind}:${year}`);
    try {
      const r = await api.seedUkTerms(year, false, kind);
      const label = kind === 'holiday_club' ? 'holiday clubs' : 'terms';
      if (r.inserted === 0) {
        toast.info(`Already seeded ${label} for ${year}`, { description: 'No new entries added.' });
      } else {
        toast.success(`Seeded ${r.inserted} ${label} for ${year}`, {
          description: kind === 'holiday_club'
            ? 'Bank holidays auto-tagged. Edit to add inset days or extras.'
            : 'Tap a term to fine-tune your half-term and inset dates.',
        });
      }
      reload();
    } catch (e) {
      toast.error('Seed failed', { description: e.message });
    } finally {
      setSeedingKey(null);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Sessions already generated stay.`)) return;
    try {
      await api.deleteTerm(t._id);
      toast.success('Deleted');
      reload();
    } catch (e) {
      toast.error('Delete failed', { description: e.message });
    }
  };

  const renderKindSection = (kind) => {
    const buckets = grouped[kind] || {};
    const ays = Object.keys(buckets).sort();
    const isHoliday = kind === 'holiday_club';
    return (
      <section key={kind} style={{ marginTop: kind === 'term' ? 0 : '1.5rem' }} data-testid={`section-${kind}`}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.4rem' }}>
          <h3 style={{ margin: 0 }}>
            {isHoliday ? '🏖️ ' : '🎓 '}{KIND_LABELS[kind]}
          </h3>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {PRESET_YEARS.map((y) => {
              const seedKey = `${kind}:${y}`;
              const isSeeding = seedingKey === seedKey;
              return (
                <button
                  key={seedKey}
                  type="button"
                  className="btn btn-ghost"
                  disabled={!!seedingKey}
                  onClick={() => handleSeed(y, kind)}
                  data-testid={`seed-${kind}-${y}`}
                  style={{ fontSize: '0.78rem', padding: '0.25rem 0.55rem' }}
                >
                  {isSeeding ? '…' : `🌱 Seed UK ${y}${isHoliday ? ' clubs' : ''}`}
                </button>
              );
            })}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setEditing({ kind })}
              data-testid={`add-${kind}-btn`}
              style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
            >
              + Add {isHoliday ? 'holiday club' : 'term'}
            </button>
          </div>
        </div>

        {ays.length === 0 ? (
          <div className="card empty-state" style={{ padding: '0.85rem' }} data-testid={`empty-${kind}`}>
            <p style={{ margin: 0, fontSize: '0.85rem' }}>
              {isHoliday
                ? 'No holiday clubs yet. Click 🌱 Seed UK to populate the gap windows (half-term, Christmas, Easter, Summer) with bank holidays already tagged.'
                : 'No terms configured yet. Click 🌱 Seed UK to start.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {ays.map((ay) => (
              <div key={ay} className="card" data-testid={`group-${kind}-${ay}`}>
                <h4 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '0.95rem' }}>Academic year {ay}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.6rem' }}>
                  {buckets[ay].map((t) => (
                    <div key={t._id} className="card" style={{ background: 'hsl(var(--muted)/0.4)' }} data-testid={`term-card-${t._id}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
                        <strong style={{ fontSize: '0.9rem' }}>{t.name}</strong>
                        {t.is_preset_seeded && (
                          <span style={{ fontSize: '0.65rem', padding: '0.05rem 0.4rem', borderRadius: 999, background: 'hsl(var(--background))', color: 'hsl(var(--muted-foreground))' }}>
                            UK preset
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.82rem', marginTop: 4 }}>
                        📆 {t.start_date} → {t.end_date}
                      </div>
                      {(t.closed_dates || []).length > 0 ? (
                        <div style={{ fontSize: '0.76rem', marginTop: 4, color: 'hsl(var(--muted-foreground))' }}>
                          Closed: {(t.closed_dates || []).map((c) => `${c.label || c.type} (${c.start}${c.end !== c.start ? `–${c.end}` : ''})`).join(', ')}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.76rem', marginTop: 4, color: 'hsl(var(--muted-foreground))' }}>
                          No closures recorded.
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem' }}>
                        <button type="button" className="btn btn-ghost" onClick={() => setEditing(t)} data-testid={`edit-term-${t._id}`}
                          style={{ fontSize: '0.76rem', padding: '0.2rem 0.5rem' }}
                        >Edit</button>
                        <button type="button" className="btn btn-ghost" onClick={() => handleDelete(t)} data-testid={`delete-term-${t._id}`}
                          style={{ fontSize: '0.76rem', padding: '0.2rem 0.5rem', color: 'hsl(var(--destructive))' }}
                        >Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  };

  return (
    <div data-testid="term-calendar-manager">
      <div style={{ marginBottom: '0.85rem' }}>
        <h2 style={{ margin: 0 }}>📅 Term Calendar</h2>
        <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
          Defines your provider's term dates, closed days, and holiday-club windows. Used to auto-generate term-time and holiday-club sessions and skip closures in parent recurring bookings.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {loading && <p style={{ color: 'hsl(var(--muted-foreground))' }}>Loading…</p>}

      {!loading && (
        <>
          {renderKindSection('term')}
          {renderKindSection('holiday_club')}
        </>
      )}

      {editing && (
        <TermEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}


function TermEditor({ initial, onClose, onSaved }) {
  const isNew = !initial?._id;
  const initialKind = initial?.kind || 'term';

  const [form, setForm] = useState({
    kind: initialKind,
    academic_year: initial?.academic_year || '2025-26',
    term: initial?.term || (initialKind === 'holiday_club' ? 'autumn_half_term' : 'autumn'),
    name: initial?.name || '',
    start_date: initial?.start_date || '',
    end_date: initial?.end_date || '',
    closed_dates: initial?.closed_dates || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isHoliday = form.kind === 'holiday_club';

  const updateClosed = (idx, key, value) => setForm((f) => {
    const next = [...f.closed_dates];
    next[idx] = { ...next[idx], [key]: value };
    if (key === 'start' && (!next[idx].end || next[idx].end < value)) next[idx].end = value;
    return { ...f, closed_dates: next };
  });
  const addClosed = () => setForm((f) => ({
    ...f,
    closed_dates: [
      ...f.closed_dates,
      { start: '', end: '', label: '', type: isHoliday ? 'bank_holiday' : 'half_term' },
    ],
  }));
  const removeClosed = (idx) => setForm((f) => ({ ...f, closed_dates: f.closed_dates.filter((_, i) => i !== idx) }));

  const termOptions = isHoliday
    ? [
        { value: 'autumn_half_term', label: 'Autumn half-term' },
        { value: 'christmas', label: 'Christmas' },
        { value: 'spring_half_term', label: 'Spring half-term' },
        { value: 'easter', label: 'Easter' },
        { value: 'summer_half_term', label: 'Summer half-term' },
        { value: 'summer', label: 'Summer' },
        { value: 'other', label: 'Other' },
      ]
    : [
        { value: 'autumn', label: 'Autumn' },
        { value: 'spring', label: 'Spring' },
        { value: 'summer', label: 'Summer' },
      ];

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const closed = form.closed_dates
        .map((c) => ({ ...c, end: c.end || c.start }))
        .filter((c) => c.start);
      const fallbackName = isHoliday
        ? `${(termOptions.find((o) => o.value === form.term)?.label) || 'Holiday'} (${form.academic_year})`
        : `${form.term[0].toUpperCase()}${form.term.slice(1)} (${form.academic_year})`;
      const body = {
        kind: form.kind,
        academic_year: form.academic_year,
        term: form.term,
        name: form.name || fallbackName,
        start_date: form.start_date,
        end_date: form.end_date,
        closed_dates: closed,
      };
      if (!body.start_date || !body.end_date) {
        throw new Error('Start and end dates are required.');
      }
      if (body.start_date > body.end_date) {
        throw new Error('Start date must be on or before end date.');
      }
      if (isNew) {
        await api.createTerm(body);
        toast.success(isHoliday ? 'Holiday club created' : 'Term created');
      } else {
        await api.updateTerm(initial._id, {
          name: body.name,
          start_date: body.start_date,
          end_date: body.end_date,
          closed_dates: body.closed_dates,
          kind: body.kind,
        });
        toast.success(isHoliday ? 'Holiday club updated' : 'Term updated');
      }
      onSaved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="term-editor">
      <div className="modal-content" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid hsl(var(--border))' }}>
          <h2 style={{ margin: 0 }}>
            {isNew
              ? (isHoliday ? 'Add holiday club' : 'Add term')
              : `Edit ${initial?.name}`}
          </h2>
        </div>
        <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          {error && <div className="error-message" data-testid="term-editor-error">{error}</div>}

          {isNew && (
            <div className="form-group" style={{ margin: 0 }}>
              <label>Type</label>
              <select
                value={form.kind}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  kind: e.target.value,
                  term: e.target.value === 'holiday_club' ? 'autumn_half_term' : 'autumn',
                }))}
                data-testid="term-kind-select"
              >
                <option value="term">🎓 School term</option>
                <option value="holiday_club">🏖️ Holiday club window</option>
              </select>
            </div>
          )}

          {isNew && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Academic year</label>
                <input value={form.academic_year} onChange={(e) => setForm((f) => ({ ...f, academic_year: e.target.value }))} data-testid="term-academic-year" placeholder="2025-26" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>{isHoliday ? 'Holiday window' : 'Term'}</label>
                <select value={form.term} onChange={(e) => setForm((f) => ({ ...f, term: e.target.value }))} data-testid="term-name-select">
                  {termOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          )}

          <div className="form-group" style={{ margin: 0 }}>
            <label>Display name</label>
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={isHoliday ? 'e.g. Summer Holiday Club 2026' : 'e.g. Autumn 2025'} data-testid="term-display-name" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Start date *</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} required data-testid="term-start-date" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>End date *</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} required data-testid="term-end-date" />
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <strong style={{ fontSize: '0.9rem' }}>
                {isHoliday
                  ? 'Closed dates within this window (e.g. bank holidays)'
                  : 'Closed dates within this term (half-term, inset, bank holidays)'}
              </strong>
              <button type="button" className="btn btn-ghost" onClick={addClosed} data-testid="add-closed-date" style={{ fontSize: '0.78rem', padding: '0.2rem 0.5rem' }}>
                + Add closure
              </button>
            </div>
            {form.closed_dates.length === 0 && (
              <small style={{ color: 'hsl(var(--muted-foreground))' }}>None — sessions will run every selected weekday in the window.</small>
            )}
            {form.closed_dates.map((c, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 4, alignItems: 'flex-end', marginTop: 4 }}>
                <input type="date" value={c.start} onChange={(e) => updateClosed(idx, 'start', e.target.value)} title="Start" data-testid={`closed-${idx}-start`} />
                <input type="date" value={c.end} onChange={(e) => updateClosed(idx, 'end', e.target.value)} title="End (same date for single-day)" data-testid={`closed-${idx}-end`} />
                <select value={c.type} onChange={(e) => updateClosed(idx, 'type', e.target.value)} data-testid={`closed-${idx}-type`}>
                  {CLOSED_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input value={c.label} onChange={(e) => updateClosed(idx, 'label', e.target.value)} placeholder="Label (e.g. Christmas Day)" data-testid={`closed-${idx}-label`} />
                <button type="button" className="btn btn-ghost" onClick={() => removeClosed(idx)} title="Remove" data-testid={`closed-${idx}-remove`}>×</button>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid hsl(var(--border))', display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving} data-testid="term-editor-cancel">Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="term-editor-save">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
